// useProjectData — composite reader hook (RFC-0010 §1 Key Interfaces).
//
// Combines `GET /api/projects/:repo` (nav/metadata/deps/links, via useProject)
// with `GET /api/projects/:repo/docs?path=` (the selected doc body + frontmatter,
// via useProjectDoc). When no `path` is given it defaults to the first leaf doc
// in the nav (the first row with a non-null `docPath`), so `/projects/:repo`
// lands on a sensible default without a second request to enumerate docs.

import { useMemo } from 'react';
import type { DocDetail, ProjectDetailResponse } from '@portal/shared-types';
import { useProject } from '@/lib/queries/projects';
import { useProjectDoc } from '@/lib/queries/docs';

export interface UseProjectDataResult {
  project: ProjectDetailResponse | undefined;
  doc: DocDetail | undefined;
  /** The doc path actually being read (the requested one, or the first leaf). */
  effectivePath: string | undefined;
  /** Project-detail query state. */
  isProjectLoading: boolean;
  isProjectError: boolean;
  projectError: unknown;
  /** Doc-detail query state (only meaningful once a path is resolvable). */
  isDocLoading: boolean;
  isDocError: boolean;
  docError: unknown;
  refetch: () => void;
}

export function useProjectData(
  repo: string | undefined,
  path: string | undefined,
): UseProjectDataResult {
  const projectQuery = useProject(repo);
  const project = projectQuery.data;

  // Default to the first nav leaf (non-null docPath) when no path is requested.
  const firstLeafPath = useMemo(
    () => project?.nav.find((n) => n.docPath !== null)?.docPath ?? undefined,
    [project],
  );
  const effectivePath = path ?? firstLeafPath;

  const docQuery = useProjectDoc(repo, effectivePath);

  return {
    project,
    doc: docQuery.data,
    effectivePath,
    isProjectLoading: projectQuery.isLoading,
    isProjectError: projectQuery.isError,
    projectError: projectQuery.error,
    isDocLoading: docQuery.isLoading && docQuery.fetchStatus !== 'idle',
    isDocError: docQuery.isError,
    docError: docQuery.error,
    refetch: () => {
      void projectQuery.refetch();
      void docQuery.refetch();
    },
  };
}
